import React, { useEffect, useState, useCallback } from 'react';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';
import { getTickets, getTicket, createTicket, replyToTicket } from '../utils/api';
import { formatDate, formatDistanceToNow } from '../utils/helpers';
import toast from 'react-hot-toast';
import {
  Plus, LifeBuoy, ChevronRight, Clock, CheckCircle2,
  AlertCircle, Circle, Send, Tag, X, ArrowLeft,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'billing',  label: 'Billing' },
  { value: 'bug',      label: 'Bug / Error' },
  { value: 'account',  label: 'Account' },
  { value: 'feature',  label: 'Feature Request' },
  { value: 'general',  label: 'General Question' },
];
const PRIORITIES = [
  { value: 'low',    label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function StatusBadge({ status }) {
  const map = {
    open:        { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'Open' },
    in_progress: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'In Progress' },
    waiting:     { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', label: 'Waiting on You' },
    resolved:    { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  label: 'Resolved' },
    closed:      { color: '#6b7280', bg: 'rgba(107,114,128,0.1)', label: 'Closed' },
  };
  const s = map[status] || map.open;
  return (
    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, background: s.bg, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function PriorityDot({ priority }) {
  const colors = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' };
  return <span style={{ width: 7, height: 7, borderRadius: '50%', background: colors[priority] || colors.normal, display: 'inline-block', marginRight: 4 }} />;
}

function TicketThread({ ticket, onBack, onReply, onStatusChange }) {
  const { user }   = useAuth();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket.messages]);

  const handleSend = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await onReply(ticket.id, reply.trim());
      setReply('');
    } finally { setSending(false); }
  };

  const isClosed = ['resolved','closed'].includes(ticket.status);
  const myName   = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Thread header */}
      <div style={{ padding: '0 0 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #6b7280)', padding: '4px 2px', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', flexShrink: 0 }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{ticket.subject}</span>
            <StatusBadge status={ticket.status} />
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #6b7280)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span><Tag size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />{CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}</span>
            <span><Clock size={10} style={{ marginRight: 3, verticalAlign: '-1px' }} />Opened {formatDistanceToNow(ticket.created_at)}</span>
            {ticket.resolved_at && <span><CheckCircle2 size={10} style={{ marginRight: 3, verticalAlign: '-1px', color: '#22c55e' }} />Resolved {formatDate(ticket.resolved_at)}</span>}
          </div>
        </div>
        {!isClosed && (
          <button
            onClick={() => onStatusChange(ticket.id, 'closed')}
            style={{ fontSize: '0.7rem', padding: '5px 10px', background: 'none', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary, #6b7280)', flexShrink: 0 }}
          >
            Close ticket
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(ticket.messages || []).map(msg => {
          const isAdmin = msg.sender_type === 'admin';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: isAdmin ? 'row' : 'row-reverse', gap: 10, alignItems: 'flex-start' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: isAdmin ? 'rgba(212,168,83,0.15)' : 'var(--zeus-blue, #1e3a5f)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 700,
                color: isAdmin ? '#d4a853' : '#fff',
                border: isAdmin ? '1px solid rgba(212,168,83,0.3)' : 'none',
              }}>
                {isAdmin ? 'S' : (msg.sender_name || 'U')[0].toUpperCase()}
              </div>
              <div style={{ maxWidth: '75%' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: isAdmin ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                  background: isAdmin ? 'var(--bg-secondary, #f8fafc)' : 'var(--zeus-blue, #1e3a5f)',
                  color: isAdmin ? 'var(--text-primary, #1e293b)' : '#fff',
                  fontSize: '0.85rem', lineHeight: 1.6, border: isAdmin ? '1px solid var(--border-color, #e5e7eb)' : 'none',
                }}>
                  {msg.body}
                </div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary, #6b7280)', marginTop: 3, textAlign: isAdmin ? 'left' : 'right' }}>
                  {isAdmin ? 'Support Team' : msg.sender_name} · {formatDistanceToNow(msg.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {isClosed ? (
        <div style={{ padding: '12px 0 0', borderTop: '1px solid var(--border-color, #e5e7eb)', fontSize: '0.78rem', color: 'var(--text-secondary, #6b7280)', textAlign: 'center' }}>
          This ticket is closed. <a href="#new" onClick={e => { e.preventDefault(); onBack(); }} style={{ color: 'var(--zeus-blue, #1e3a5f)' }}>Open a new ticket</a> if you need more help.
        </div>
      ) : (
        <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--border-color, #e5e7eb)',
                background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1e293b)',
                fontSize: '0.85rem', lineHeight: 1.6, resize: 'none',
                outline: 'none', fontFamily: 'inherit', minHeight: 72,
              }}
              placeholder="Type your reply…"
              value={reply}
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
            />
            <button
              onClick={handleSend}
              disabled={!reply.trim() || sending}
              style={{
                padding: '10px 16px', background: 'var(--zeus-blue, #1e3a5f)', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: 6, fontSize: '0.82rem', fontWeight: 600,
                opacity: !reply.trim() || sending ? 0.5 : 1,
              }}
            >
              <Send size={13} /> Send
            </button>
          </div>
          <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary, #6b7280)', marginTop: 4 }}>Cmd+Enter to send</div>
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets,       setTickets]      = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [activeTicket,  setActiveTicket] = useState(null);
  const [showNewModal,  setShowNewModal] = useState(false);
  const [statusFilter,  setStatusFilter] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'normal', body: '' });
  const [submitting,    setSubmitting]   = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTickets(statusFilter ? { status: statusFilter } : {});
      setTickets(data.tickets || []);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const loadThread = useCallback(async (id) => {
    try {
      const data = await getTicket(id);
      setActiveTicket(data);
    } catch { toast.error('Failed to load ticket'); }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleSubmit = async () => {
    if (!form.subject.trim()) return toast.error('Subject is required');
    if (!form.body.trim())    return toast.error('Please describe your issue');
    setSubmitting(true);
    try {
      await createTicket(form);
      toast.success('Ticket submitted — our team will respond shortly');
      setShowNewModal(false);
      setForm({ subject: '', category: 'general', priority: 'normal', body: '' });
      await loadTickets();
    } catch (err) { toast.error(err.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleReply = async (ticketId, body) => {
    try {
      await replyToTicket(ticketId, body);
      await loadThread(ticketId);
      await loadTickets();
      toast.success('Reply sent');
    } catch (err) { toast.error(err.message || 'Failed to send reply'); }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      // Ticket status changes come from admin side; CMS users can only close
      await replyToTicket(ticketId, '[Ticket closed by user]');
      setActiveTicket(null);
      await loadTickets();
    } catch { toast.error('Failed to close ticket'); }
  };

  const openTickets   = tickets.filter(t => !['resolved','closed'].includes(t.status));
  const closedTickets = tickets.filter(t => ['resolved','closed'].includes(t.status));

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #1e293b)', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: 5, fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em' };

  const TicketRow = ({ ticket }) => {
    const isWaiting = ticket.status === 'waiting';
    return (
      <div
        onClick={() => loadThread(ticket.id).then(() => {})}
        style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
          transition: 'background 0.1s',
          background: isWaiting ? 'rgba(139,92,246,0.04)' : 'transparent',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary, #f8fafc)'}
        onMouseLeave={e => e.currentTarget.style.background = isWaiting ? 'rgba(139,92,246,0.04)' : 'transparent'}
      >
        <LifeBuoy size={16} style={{ color: 'var(--text-secondary, #9ca3af)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ticket.subject}
            </span>
            <StatusBadge status={ticket.status} />
            {isWaiting && <span style={{ fontSize: '0.65rem', color: '#8b5cf6', fontWeight: 700 }}>● Reply needed</span>}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary, #6b7280)', display: 'flex', gap: 10 }}>
            <span>{CATEGORIES.find(c => c.value === ticket.category)?.label}</span>
            <span><PriorityDot priority={ticket.priority} />{ticket.priority}</span>
            <span>{formatDistanceToNow(ticket.last_reply_at || ticket.created_at)}</span>
          </div>
        </div>
        <ChevronRight size={14} style={{ color: 'var(--text-secondary, #9ca3af)', flexShrink: 0 }} />
      </div>
    );
  };

  return (
    <>
      <Header title="Support">
        <button
          onClick={() => setShowNewModal(true)}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: 'var(--zeus-blue, #1e3a5f)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
        >
          <Plus size={14} /> New Ticket
        </button>
      </Header>

      <div style={{ padding: '24px', maxWidth: 860, margin: '0 auto' }}>

        {activeTicket ? (
          /* Thread view */
          <div style={{ background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, padding: 24, minHeight: 500, display: 'flex', flexDirection: 'column' }}>
            <TicketThread
              ticket={activeTicket}
              onBack={() => { setActiveTicket(null); loadTickets(); }}
              onReply={handleReply}
              onStatusChange={handleStatusChange}
            />
          </div>
        ) : (
          /* Ticket list */
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Open Tickets',     value: tickets.filter(t => t.status === 'open').length,        color: '#3b82f6' },
                { label: 'Awaiting Reply',   value: tickets.filter(t => t.status === 'waiting').length,     color: '#8b5cf6' },
                { label: 'Resolved',         value: tickets.filter(t => t.status === 'resolved').length,    color: '#22c55e' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Filter */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[['','All'], ['open','Open'], ['in_progress','In Progress'], ['waiting','Waiting'], ['resolved','Resolved']].map(([v, l]) => (
                <button key={v} onClick={() => setStatusFilter(v)} style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid var(--border-color, #e5e7eb)', background: statusFilter === v ? 'var(--zeus-blue, #1e3a5f)' : 'transparent', color: statusFilter === v ? '#fff' : 'var(--text-secondary, #6b7280)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s' }}>
                  {l}
                </button>
              ))}
            </div>

            <div style={{ background: 'var(--bg-primary, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden' }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary, #6b7280)' }}>Loading…</div>
              ) : tickets.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <LifeBuoy size={32} style={{ color: 'var(--text-secondary, #9ca3af)', marginBottom: 12 }} />
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>No support tickets yet</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary, #6b7280)', marginBottom: 16 }}>Hit a bug or have a question? We're here to help.</div>
                  <button onClick={() => setShowNewModal(true)} style={{ padding: '8px 18px', background: 'var(--zeus-blue, #1e3a5f)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                    Open a Ticket
                  </button>
                </div>
              ) : (
                <>
                  {openTickets.length > 0 && openTickets.map(t => <TicketRow key={t.id} ticket={t} />)}
                  {closedTickets.length > 0 && (
                    <>
                      <div style={{ padding: '10px 16px', fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-secondary, #9ca3af)', textTransform: 'uppercase', letterSpacing: '0.1em', background: 'var(--bg-secondary, #f8fafc)', borderTop: openTickets.length ? '1px solid var(--border-color, #e5e7eb)' : 'none' }}>
                        Closed / Resolved
                      </div>
                      {closedTickets.map(t => <TicketRow key={t.id} ticket={t} />)}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewModal && (
        <Modal onClose={() => setShowNewModal(false)}>
          <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Open a Support Ticket</h2>
              <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary, #6b7280)' }}>Our team typically responds within 1 business day.</p>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={inp}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Subject</label>
              <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief summary of the issue" style={inp} autoFocus />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Description</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Describe the issue in detail — what happened, what you expected, and any steps to reproduce…"
                style={{ ...inp, resize: 'vertical', minHeight: 120, lineHeight: 1.6 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowNewModal(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={submitting} style={{ padding: '8px 18px', background: 'var(--zeus-blue, #1e3a5f)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, opacity: submitting ? 0.6 : 1 }}>
                {submitting ? 'Submitting…' : 'Submit Ticket'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
